import { useCallback, useEffect } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { $inquiriesStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { TravelStatus } from '@widgets';
import { Icon, Table, TableCellType, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface MyRequestsProps {
  className?: string;
}

export const MyRequests = (props: MyRequestsProps) => {
  const { navigate } = useRouteHook();

  const inquiriesStore = useUnit($inquiriesStore.store);

  const listState = useListState({ list: 'inquiries' });

  const { t } = useTranslation('travel');

  useEffect(() => {
    return () => $inquiriesStore.events.clear();
  }, []);

  const getInquiries = useCallback(() => {
    $inquiriesStore.events.getInquiries({ returnTotalCount: true, count: listState.count, fromIndex: listState.fromIndex, page: listState.page });
  }, [listState.count, listState.fromIndex, listState.page]);

  useEffect(() => {
    getInquiries();
  }, [getInquiries]);

  return (
    <section
      className={cn(
        'flex h-fit flex-col gap-6 bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
        props.className,
      )}
    >
      <Text tag='h2' weight='semibold' size='2xl'>
        {t('myRequests.title')}
      </Text>
      <Table
        isLoading={inquiriesStore.loading}
        error={inquiriesStore.error}
        onRefresh={getInquiries}
        isScrollable
        columns={[t('myRequests.columns.reference'), t('myRequests.columns.submitted'), t('myRequests.columns.status'), '']}
        items={inquiriesStore.data.data.map((inquiry) => [
          { type: TableCellType.Text, value: inquiry.name, className: 'font-semibold' },
          { type: TableCellType.Text, value: dayjs(inquiry.createdOn).format('DD MMM YYYY') },
          { type: TableCellType.Custom, value: <TravelStatus status={inquiry.status} /> },
          { type: TableCellType.Custom, value: <Icon icon={<ChevronIcon className='ml-auto size-4 rotate-180' />} /> },
        ])}
        pagination={{
          currentPage: listState.page,
          countItemPage: listState.count,
          totalItemCount: inquiriesStore.data.total,
          onChangePage: listState.onChangePage,
          onChangeCountItemPage: listState.onChangeCount,
        }}
        onClick={(rowIndex) =>
          inquiriesStore.data.data?.[rowIndex]?.inquiryId &&
          navigate(RoutePaths.travelMyRequest.onNavigate(inquiriesStore.data.data[rowIndex].inquiryId))
        }
      />
    </section>
  );
};
